<template>
  <ErrorState
    v-if="fetchError && !document"
    title="Couldn't load supplier"
    :message="fetchError"
    @retry="subscribe"
  />
  <div v-else-if="notFound" class="space-y-4 py-8">
    <ErrorState
      title="Supplier not found"
      message="This supplier may have been deleted, or the link is invalid."
      :show-retry="false"
    />
    <div class="flex justify-center">
      <router-link
        :to="{ name: 'Suppliers' }"
        class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">arrow_back</span>
        Back to Suppliers
      </router-link>
    </div>
  </div>
  <div v-else-if="!document">
    <spinner-view />
  </div>
  <div v-else class="supplier-view space-y-6">
    <AlertBanner
      v-if="fetchError"
      type="error"
      message="Couldn't refresh supplier — showing the last loaded data."
      :dismissible="false"
    />
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400 mb-1" aria-label="breadcrumb">
          <router-link :to="{ name: 'Suppliers' }" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Suppliers</router-link>
          <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
          <span class="text-neutral-900 dark:text-neutral-100 font-medium">{{ document.suppliername }}</span>
        </nav>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
            <span class="text-lg font-bold text-primary-700 dark:text-primary-300">{{ document.suppliername?.charAt(0)?.toUpperCase() }}</span>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">{{ document.suppliername }}</h1>
            <p class="text-sm text-neutral-500 dark:text-neutral-400">ID: {{ document.supplierid }}</p>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
          @click="showEditSupplier = true"
        >
          <span class="material-icons text-[18px]" aria-hidden="true">edit</span>
          Edit Supplier
        </button>
      </div>
    </div>

    <div>

      <!-- Odoo Integration Card -->
      <div v-if="document.odooId" class="mb-6">
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-emerald-200 dark:border-emerald-800/40 overflow-hidden">
          <div class="px-6 py-4 border-b border-emerald-100 dark:border-emerald-800/40 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="material-icons text-emerald-600 dark:text-emerald-400" aria-hidden="true">sync_alt</span>
              <h2 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Odoo Integration</h2>
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Linked
              </span>
            </div>
            <button
              @click="showUnlinkConfirm = true"
              :disabled="unlinkingFromDetail"
              class="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-sm font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
            >
              {{ unlinkingFromDetail ? 'Unlinking...' : 'Unlink from Odoo' }}
            </button>
          </div>
          <div class="px-6 py-5">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Odoo Partner ID</span>
                <p class="text-sm text-neutral-900 dark:text-neutral-100 font-medium mt-0.5">{{ document.odooId }}</p>
              </div>
              <div v-if="document.odooName">
                <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Odoo Legal Name</span>
                <p class="text-sm text-neutral-900 dark:text-neutral-100 font-medium mt-0.5">{{ document.odooName }}</p>
              </div>
              <div>
                <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">OMS Operational Name</span>
                <p class="text-sm text-neutral-900 dark:text-neutral-100 font-medium mt-0.5">{{ document.suppliername }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Supplier Details and Contact Information -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Supplier Details Card -->
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
            <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Supplier Details</h2>
          </div>
          <div class="px-6 py-5 space-y-3">
            <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Supplier ID:</span>
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{{ document.supplierid }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Supplier Number:</span>
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{{ document.suppliernumber }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Supplier Name:</span>
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{{ document.suppliername }}</span>
            </div>
            <div v-if="!document.locations.length" class="flex justify-between py-2">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Zone Name:</span>
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">
                {{ document.zoneName != null ? document.zoneName : 'Unlisted' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Contact Information Card -->
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
            <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Contact Information</h2>
          </div>
          <div class="px-6 py-5 space-y-3">
            <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Contact Person:</span>
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{{ document.contactname }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Email Address:</span>
              <span class="text-sm text-blue-600 dark:text-blue-400 font-medium">{{ document.contactemailaddress }}</span>
            </div>
            <div class="flex justify-between py-2">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Phone Number:</span>
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{{ document.contacttelephone }}</span>
            </div>
            <div v-if="document.emailaddress" class="flex justify-between py-2 border-t border-neutral-100 dark:border-neutral-700">
              <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">Ops/Receipt Email:</span>
              <span class="text-sm text-blue-600 dark:text-blue-400 font-medium">{{ document.emailaddress }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Map and Settings Section -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        <!-- Location Map Card -->
        <div
          v-if="
            document.geotag != 'NIL' &&
            document.geotag != null &&
            !document.locations.length
          "
          class="lg:col-span-3 bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden"
        >
          <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
            <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Location Map</h2>
          </div>
          <div class="p-4">
            <div class="h-72 md:h-96 lg:h-[600px] rounded-lg overflow-hidden">
              <GoogleMap
                api-key="AIzaSyAhURDudHSVdIaghKlgWaE4BNAw_hdzPSE"
                style="width: 100%; height: 100%"
                :center="center"
                :zoom="15"
              >
                <Marker :options="{ position: center }" />
              </GoogleMap>
            </div>
          </div>
        </div>

        <!-- Supplier Settings Column -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Recurring Collection Card -->
          <div v-if="!document.locations.length" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Recurring Collection</h2>
            </div>
            <div class="px-6 py-5">
              <form @submit.prevent="updateReccuring" class="space-y-4">
                <label class="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 cursor-pointer">
                  <input type="checkbox" v-model="fixedMode" class="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                  Fixed weekdays (weekly on selected days; overrides zone days)
                </label>
                <div v-if="!fixedMode" class="grid grid-cols-2 gap-4">
                  <div>
                    <label for="recurring-duration" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Duration</label>
                    <select
                      id="recurring-duration"
                      v-model="recurringDuration"
                      required
                      class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                    </select>
                  </div>
                </div>
                <div v-if="fixedMode">
                  <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Collection Weekdays</label>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      v-for="d in weekdayChips"
                      :key="d.value"
                      type="button"
                      @click="toggleFixedDay(d.value)"
                      :aria-pressed="fixedWeekdays.includes(d.value).toString()"
                      :class="[
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                        fixedWeekdays.includes(d.value)
                          ? 'bg-primary-700 text-white shadow-sm'
                          : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                      ]"
                    >{{ d.label }}</button>
                  </div>
                  <p v-if="!fixedWeekdays.length" class="text-xs text-red-600 dark:text-red-400 mt-1">Select at least one weekday</p>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div v-if="!fixedMode">
                    <label for="recurring-frequency" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Frequency</label>
                    <select
                      id="recurring-frequency"
                      v-model="recurringFrequency"
                      required
                      class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm"
                    >
                      <option value="1">Days</option>
                      <option value="7">Weeks</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Start Date</label>
                    <VueDatePicker
                      required
                      teleport="body"
                      v-model="recurringStartDate"
                      :min-date="new Date()"
                      :enable-time-picker="false"
                      :dark="isDark"
                      aria-label="Start date"
                    ></VueDatePicker>
                  </div>
                </div>
                <div v-if="document.nextSchedule" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p class="text-sm text-blue-800 dark:text-blue-300">
                    <span class="font-medium">Next Schedule Date:</span>
                    {{ new Date(document.nextSchedule.toDate().toISOString().slice(0, 10) + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) }}
                  </p>
                </div>
                <button
                  type="submit"
                  class="w-full px-4 py-2.5 bg-green-700 text-white font-medium rounded-lg hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="recurringButtonLoading"
                >
                  <span v-if="!recurringButtonLoading">{{ hasRecurring ? "Update" : "Save" }}</span>
                  <span v-else class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                </button>
                <button
                  v-if="hasRecurring"
                  type="button"
                  @click="showCancelRecurringConfirm = true"
                  :disabled="cancellingRecurring"
                  class="w-full mt-2 px-4 py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-medium rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                >
                  {{ cancellingRecurring ? 'Cancelling...' : 'Cancel Recurring Collection' }}
                </button>
              </form>
            </div>
          </div>

          <!-- Collection Status Card (suggestions stop while Lost) -->
          <div v-if="!document.locations.length" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700 flex items-center justify-between">
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Collection Status</h2>
              <span :class="['px-2.5 py-0.5 rounded-full text-xs font-semibold', collectionStatus === 'lost'
                  ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300']">
                {{ collectionStatus === 'lost' ? 'Lost' : 'Active' }}
              </span>
            </div>
            <div class="px-6 py-5 space-y-3">
              <p class="text-sm text-neutral-600 dark:text-neutral-400">
                <template v-if="collectionStatus === 'lost'">
                  Marked lost{{ document.collectionStatusChangedBy ? ` by ${document.collectionStatusChangedBy}` : '' }}. No collection suggestions are made for this customer.
                </template>
                <template v-else>Active customers without a recurring schedule receive collection suggestions based on their history.</template>
              </p>
              <button type="button" @click="toggleCollectionStatus" :disabled="collectionStatusBusy"
                :class="['w-full px-4 py-2.5 font-medium rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-neutral-900',
                  collectionStatus === 'lost'
                    ? 'bg-emerald-700 text-white hover:bg-emerald-800 focus:ring-emerald-500'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 focus:ring-red-500']">
                {{ collectionStatusBusy ? 'Saving…' : (collectionStatus === 'lost' ? 'Reactivate customer' : 'Mark customer as lost') }}
              </button>
            </div>
          </div>

          <!-- Service Windows Card -->
          <div v-if="!document.locations.length" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Service Windows</h2>
            </div>
            <div class="px-6 py-5">
              <form @submit.prevent="updateServiceWindows" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label for="service-start-time" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Start Time</label>
                    <select
                      id="service-start-time"
                      v-model="serviceStartTime"
                      required
                      class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm"
                    >
                      <option
                        :value="time.value"
                        v-for="time in timeWindows"
                        :key="time.value"
                      >
                        {{ time.time }}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label for="service-end-time" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">End Time</label>
                    <select
                      id="service-end-time"
                      v-model="serviceEndTime"
                      required
                      :aria-invalid="serviceWindowError ? 'true' : null"
                      class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm"
                    >
                      <option
                        :value="time.value"
                        v-for="time in timeWindows"
                        :key="time.value"
                      >
                        {{ time.time }}
                      </option>
                    </select>
                    <p v-if="serviceWindowError" class="mt-1.5 text-xs text-red-600 dark:text-red-400">{{ serviceWindowError }}</p>
                  </div>
                </div>
                <button
                  type="submit"
                  class="w-full px-4 py-2.5 bg-green-700 text-white font-medium rounded-lg hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="serviceTimeButtonLoading || !!serviceWindowError"
                >
                  <span v-if="!serviceTimeButtonLoading">{{ serviceStartTime != null ? "Update" : "Save" }}</span>
                  <span v-else class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                </button>
              </form>
            </div>
          </div>

          <!-- Assets/Containers Card -->
          <div v-if="!document.locations.length" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700 flex items-center justify-between">
              <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Assets</h2>
              <button
                @click="showForm = true"
                title="Add container"
                aria-label="Add container"
                class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-600"
              >
                <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
              </button>
            </div>
            <div class="p-4">
              <DataTable
                :data="document.containers || []"
                :columns="containerColumns"
                :pagination="false"
                empty-text="No containers added yet"
              >
                <template #actions="{ row }">
                  <button
                    @click="confirmDeleteContainer(row)"
                    title="Delete container"
                    :aria-label="`Delete ${row.containerType} container`"
                    class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-600"
                  >
                    <svg class="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </template>
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      <!-- Locations Section -->
      <div v-if="document.locations.length" class="mb-6">
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
            <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Locations</h2>
          </div>
          <div class="p-4">
            <DataTable
              :data="document.locations"
              :columns="locationColumns"
              row-key="locationid"
              :pagination="true"
              empty-text="No locations found"
            >
              <template #cell-locationid="{ row }">
                <router-link
                  :to="{
                    name: 'LocationView',
                    params: {
                      locationId: row.locationid,
                      supplierId: document.supplierid,
                    },
                  }"
                  class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                >
                  {{ row.locationid }}
                </router-link>
              </template>

              <template #cell-odooStatus="{ row }">
                <span v-if="row.odooId"
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                  <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                  Linked
                </span>
                <span v-else class="text-xs text-neutral-400 dark:text-neutral-500">—</span>
              </template>

              <template #cell-serviceWindowStartTime="{ row }">
                {{
                  row.serviceWindowStartTime
                    ? row.serviceWindowStartTime + ":00"
                    : "Not Set"
                }}
              </template>

              <template #cell-serviceWindowEndTime="{ row }">
                {{
                  row.serviceWindowEndTime
                    ? row.serviceWindowEndTime + ":00"
                    : "Not Set"
                }}
              </template>
            </DataTable>
          </div>
        </div>
      </div>

      <!-- Past Orders Section -->
      <div class="mb-6">
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
            <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Past Orders</h2>
          </div>
          <div class="p-4">
            <ErrorState
              v-if="pastOrdersError"
              title="Couldn't load past orders"
              :message="pastOrdersError"
              @retry="subscribe"
            />
            <DataTable
              v-else
              :data="pastOrders"
              :columns="pastOrderColumns"
              :loading="loadingPastOrders"
              :searchable="true"
              :show-index="true"
              :pagination="true"
              loading-text="Loading past orders..."
              empty-text="No past orders found"
            >
              <template #cell-id="{ row }">
                <router-link
                  :to="{ name: 'OrderView', params: { id: row.id, supplierId: document.supplierid } }"
                  class="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                >
                  {{ row.id }}
                </router-link>
              </template>

              <template #cell-createdAt="{ row }">
                {{ row.createdAt ? row.createdAt.toDate().toLocaleString() : 'N/A' }}
              </template>

              <template #cell-collectedAt="{ row }">
                {{ row.collectedAt ? row.collectedAt.toDate().toLocaleString() : "Pending" }}
              </template>

              <template #cell-status="{ row }">
                <span
                  v-if="row.status == 'REQUESTED'"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                >
                  {{ row.status }}
                </span>
                <span
                  v-else-if="row.status == 'PLANNED'"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                >
                  {{ row.status }}
                </span>
                <span
                  v-else-if="row.status == 'SCHEDULED'"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                >
                  {{ row.status }}
                </span>
                <span
                  v-else-if="row.status == 'COMPLETED'"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-300"
                >
                  {{ row.status }}
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-300"
                >
                  {{ row.status || '—' }}
                </span>
              </template>
            </DataTable>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <div v-if="showForm">
      <AddContainer
        :showForm="showForm"
        @close="showForm = false"
        :supplierId="document.supplierid"
        :locationId="0"
      />
    </div>

    <EditSupplierModal
      :show="showEditSupplier"
      :supplier="document"
      :supplierId="id"
      @close="showEditSupplier = false"
      @saved="showEditSupplier = false"
    />

    <!-- Delete container confirmation -->
    <ConfirmDialog
      :show="!!containerToDelete"
      title="Delete container"
      :message="containerToDelete ? `Delete container type '${containerToDelete.containerType}' from this supplier? This cannot be undone.` : ''"
      confirm-label="Delete"
      :danger="true"
      :loading="deletingContainer"
      @confirm="deleteContainer"
      @cancel="containerToDelete = null"
    />

    <!-- Cancel recurring confirmation -->
    <ConfirmDialog
      :show="showCancelRecurringConfirm"
      title="Cancel recurring collection"
      message="Are you sure you want to cancel the recurring collection for this supplier?"
      confirm-label="Cancel Collection"
      cancel-label="Keep"
      :danger="true"
      :loading="cancellingRecurring"
      @confirm="cancelRecurring"
      @cancel="showCancelRecurringConfirm = false"
    />

    <!-- Unlink from Odoo confirmation -->
    <ConfirmDialog
      :show="showUnlinkConfirm"
      title="Unlink from Odoo"
      :message="`Unlink '${document.suppliername}' from Odoo? The supplier record will not be deleted.`"
      confirm-label="Unlink"
      :danger="true"
      :loading="unlinkingFromDetail"
      @confirm="handleUnlinkFromDetail"
      @cancel="showUnlinkConfirm = false"
    />
  </div>
</template>

<script>
import SpinnerView from "@/components/SpinnerView.vue";
import DataTable from "@/components/ui/DataTable.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";
import ConfirmDialog from "@/components/ui/ConfirmDialog.vue";
import { GoogleMap, Marker } from "vue3-google-map";
import AddContainer from "@/components/AddContainer.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import { useStore } from "vuex";
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  setDoc,
  query,
  where,
  deleteField,
} from "@firebase/firestore";
import { db } from "@/firebase/config";
import { ref, computed, watchEffect } from "vue";
import { useOdooApi } from "@/composables/useOdooApi";
import useSuggestionsApi from "@/composables/useSuggestionsApi";
import useToast from "@/composables/useToast";
import useDarkMode from "@/composables/useDarkMode";
import EditSupplierModal from "@/components/EditSupplierModal.vue";

export default {
  components: {
    SpinnerView,
    DataTable,
    ErrorState,
    AlertBanner,
    ConfirmDialog,
    GoogleMap,
    Marker,
    AddContainer,
    VueDatePicker,
    EditSupplierModal,
  },
  props: ["id"],
  setup(props) {
    const store = useStore();
    store.commit("setCurrentRoute", "Suppliers");
    const { showToast } = useToast();
    const { isDark } = useDarkMode();
    const document = ref(null);
    const fetchError = ref(null);
    const notFound = ref(false);
    const pastOrders = ref([]);
    const loadingPastOrders = ref(true);
    const pastOrdersError = ref(null);
    const center = ref(null);
    const showForm = ref(false);
    // const selectedLocationId = ref(0)
    // A schedule exists when frequency is set — drives Update-vs-Save and Cancel visibility.
    const hasRecurring = ref(false);
    // Fixed-weekday mode: weekly on exactly these weekdays (JS 0=Sun..6=Sat).
    // Takes precedence over the zone's operational-day lock — the operator's
    // explicit choice wins (ops rule, 2026-08-22).
    const fixedMode = ref(false);
    const fixedWeekdays = ref([]);
    const weekdayChips = [
      { label: "Mo", value: 1 }, { label: "Tu", value: 2 }, { label: "We", value: 3 },
      { label: "Th", value: 4 }, { label: "Fr", value: 5 }, { label: "Sa", value: 6 },
      { label: "Su", value: 0 },
    ];
    const toggleFixedDay = (d) => {
      const i = fixedWeekdays.value.indexOf(d);
      if (i === -1) fixedWeekdays.value.push(d);
      else fixedWeekdays.value.splice(i, 1);
    };

    const recurringDuration = ref(null);
    const recurringFrequency = ref(null);
    const recurringStartDate = ref(null);
    const recurringButtonLoading = ref(false);
    const dateList = ref([]);

    const serviceStartTime = ref(null);
    const serviceEndTime = ref(null);
    const serviceTimeButtonLoading = ref(false);

    const showEditSupplier = ref(false);
    const cancellingRecurring = ref(false);
    const showCancelRecurringConfirm = ref(false);
    const showUnlinkConfirm = ref(false);
    const containerToDelete = ref(null);
    const deletingContainer = ref(false);



    const timeWindows = [
      { value: 6, time: "06:00" },
      { value: 7, time: "07:00" },
      { value: 8, time: "08:00" },
      { value: 9, time: "09:00" },
      { value: 10, time: "10:00" },
      { value: 11, time: "11:00" },
      { value: 12, time: "12:00" },
      { value: 13, time: "13:00" },
      { value: 14, time: "14:00" },
      { value: 15, time: "15:00" },
      { value: 16, time: "16:00" },
      { value: 17, time: "17:00" },
      { value: 18, time: "18:00" },
    ];



    let docRef = doc(db, "suppliers", props.id);
    let colRefOrders = collection(db, "collectionRequests");
    colRefOrders = query(colRefOrders, where("supplierId", "==", props.id));

    let unsub = null;
    let unsub2 = null;
    const subscribe = () => {
      if (unsub) unsub();
      if (unsub2) unsub2();
      fetchError.value = null;
      notFound.value = false;
      pastOrdersError.value = null;

      unsub = onSnapshot(
        docRef,
        (doc) => {
          if (doc.data()) {
            notFound.value = false;
            fetchError.value = null;
            // Prefer stored lat/lng on the supplier doc — the migration to
            // lat/lng-first populated this for every real supplier. Falling
            // back to /getLatLng would re-hit Google Geocoding, which we're
            // retiring. If stored coords are missing, surface that as a
            // data-quality signal instead of silently paying for a geocode.
            if (
              !doc.data().locations.length &&
              typeof doc.data().lat === "number" &&
              typeof doc.data().lng === "number"
            ) {
              center.value = { lat: doc.data().lat, lng: doc.data().lng };
            }
            if (doc.data().frequency) {
              recurringDuration.value = doc.data().duration;
              recurringFrequency.value = doc.data().frequency;
              fixedWeekdays.value = [...(doc.data().fixedWeekdays || [])];
              fixedMode.value = fixedWeekdays.value.length > 0;
              hasRecurring.value = doc.data().frequency != null || fixedMode.value;
              recurringStartDate.value = doc.data().recurringStartDate;
            }

            if (doc.data().serviceWindowStartTime) {
              serviceEndTime.value = doc.data().serviceWindowEndTime;
              serviceStartTime.value = doc.data().serviceWindowStartTime;
            }
            document.value = { ...doc.data() };
          } else {
            // Deleted supplier or bad deep link — render not-found, not a spinner.
            notFound.value = true;
          }
        },
        (err) => {
          fetchError.value = err.message;
        }
      );

      unsub2 = onSnapshot(
        colRefOrders,
        (snapshot) => {
          let results = [];
          snapshot.docs.forEach((doc) => {
            if (document.value) {
              if (doc.data().supplierId == props.id) {
                results.push({ ...doc.data(), id: doc.id });
              }
            }
          });

          pastOrders.value = results;
          loadingPastOrders.value = false;
          pastOrdersError.value = null;
        },
        (err) => {
          pastOrdersError.value = err.message;
          loadingPastOrders.value = false;
        }
      );
    };
    subscribe();

    const confirmDeleteContainer = (container) => {
      containerToDelete.value = container;
    };

    const deleteContainer = async () => {
      if (!containerToDelete.value) return;
      deletingContainer.value = true;
      try {
        const containers = document.value.containers.filter(
          (container) =>
            container.containerType != containerToDelete.value.containerType
        );
        await updateDoc(docRef, { containers: containers });
        containerToDelete.value = null;
        showToast("success", "Container deleted");
      } catch (e) {
        showToast("error", "Failed to delete container. Please try again.");
      } finally {
        deletingContainer.value = false;
      }
    };

    watchEffect((onInvalidate) => {
      onInvalidate(() => {
        if (unsub) unsub();
        if (unsub2) unsub2();
      });
    });

    // The user picked a calendar day in their own timezone; persist that DAY
    // as midnight UTC (date-part == Dubai operational day, system convention).
    const pickedDayUtc = (d) => {
      if (!d) return d;
      // The loaded value is a Firestore Timestamp (has .toDate()); a re-picked
      // value is a plain Date. new Date(Timestamp) is Invalid Date, so an
      // Update without re-picking the date would save garbage without this.
      const dt = typeof d.toDate === "function" ? d.toDate() : new Date(d);
      return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    };

    const updateReccuring = async () => {
      if (fixedMode.value && !fixedWeekdays.value.length) {
        showToast("error", "Select at least one weekday for a fixed-weekday schedule.");
        return;
      }
      recurringButtonLoading.value = true;

      // Store the PICKED calendar day as midnight UTC — the system-wide
      // Dubai-day bucket. The raw picker Date is local midnight, whose UTC
      // date-part shifts a day for operators east of UTC (Maldives etc.),
      // which would anchor the whole recurrence on the wrong day.
      // Saving one mode deletes the other mode's fields so the scheduler
      // never sees an ambiguous schedule.
      const recurringOrder = fixedMode.value
        ? {
            fixedWeekdays: [...fixedWeekdays.value].sort((a, b) => a - b),
            duration: deleteField(),
            frequency: deleteField(),
            lastSchedule: null,
            nextSchedule: pickedDayUtc(recurringStartDate.value),
            recurringStartDate: pickedDayUtc(recurringStartDate.value),
          }
        : {
            duration: parseInt(recurringDuration.value),
            frequency: parseInt(recurringFrequency.value),
            fixedWeekdays: deleteField(),
            lastSchedule: null,
            nextSchedule: pickedDayUtc(recurringStartDate.value),
            recurringStartDate: pickedDayUtc(recurringStartDate.value),
          };

      try {
        await setDoc(docRef, recurringOrder, { merge: true });
        hasRecurring.value = true;
        showToast("success", "Recurring collection updated");
      } catch (e) {
        showToast("error", "Failed to update recurring collection. Please try again.");
      } finally {
        recurringButtonLoading.value = false;
      }
    };

    // Cross-field check: an end hour at or before the start hour would write
    // an inverted window that misleads route planning downstream.
    const serviceWindowError = computed(() => {
      if (serviceStartTime.value == null || serviceEndTime.value == null) return "";
      return serviceEndTime.value <= serviceStartTime.value
        ? "End time must be after start time"
        : "";
    });

    const updateServiceWindows = async () => {
      if (serviceWindowError.value) return;
      serviceTimeButtonLoading.value = true;

      const serviceWindows = {
        serviceWindowStartTime: serviceStartTime.value,
        serviceWindowEndTime: serviceEndTime.value,
      };

      try {
        await setDoc(docRef, serviceWindows, { merge: true });
        showToast("success", "Service window updated");
      } catch (e) {
        showToast("error", "Failed to update service window. Please try again.");
      } finally {
        serviceTimeButtonLoading.value = false;
      }
    };

    const cancelRecurring = async () => {
      cancellingRecurring.value = true;
      try {
        const res = await fetch(`${store.state.apiUrl}/suppliers/${props.id}/recurring`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to cancel recurring');
        hasRecurring.value = false;
        fixedWeekdays.value = [];
        fixedMode.value = false;
        recurringDuration.value = null;
        recurringFrequency.value = null;
        recurringStartDate.value = null;
        showCancelRecurringConfirm.value = false;
        showToast("success", "Recurring collection cancelled");
      } catch (e) {
        showToast("error", "Failed to cancel recurring collection. Please try again.");
      } finally {
        cancellingRecurring.value = false;
      }
    };

    const locationColumns = [
      { key: "locationid", label: "Location ID", sortable: true, align: "center" },
      { key: "locationname", label: "Location Name", sortable: true, align: "center" },
      { key: "areaname", label: "Geo Tag", sortable: true, align: "center" },
      { key: "odooStatus", label: "Odoo", sortable: false, align: "center" },
      { key: "serviceWindowStartTime", label: "Start Time", sortable: true, align: "center" },
      { key: "serviceWindowEndTime", label: "End Time", sortable: true, align: "center" },
    ];

    const containerColumns = [
      { key: "containerType", label: "Container Type", sortable: true, align: "center" },
      { key: "maxVolume", label: "Volume", sortable: true, align: "center" },
      { key: "quantity", label: "Quantity", sortable: true, align: "center" },
    ];

    const pastOrderColumns = [
      { key: "id", label: "Order ID", sortable: true, align: "center" },
      { key: "createdAt", label: "Requested Date", sortable: true, align: "center" },
      { key: "collectedAt", label: "Collected Date", sortable: true, align: "center" },
      { key: "actualVolume", label: "Volume", sortable: true, align: "center" },
      { key: "status", label: "Status", sortable: true, align: "center" },
    ];

    // Active/Lost collection status (suggestions stop while Lost)
    const { setCollectionStatus } = useSuggestionsApi();
    const collectionStatusBusy = ref(false);
    const collectionStatus = computed(() => (document.value && document.value.collectionStatus) || "active");
    const toggleCollectionStatus = async () => {
      const next = collectionStatus.value === "lost" ? "active" : "lost";
      collectionStatusBusy.value = true;
      try {
        await setCollectionStatus(props.id, next, null);
        document.value = { ...document.value, collectionStatus: next, collectionStatusChangedBy: store.state.user && store.state.user.email };
        showToast("success", next === "lost" ? "Customer marked as lost" : "Customer reactivated");
      } catch (error) {
        showToast("error", "Failed to update collection status. Please try again.");
      } finally {
        collectionStatusBusy.value = false;
      }
    };

    // Odoo unlink from detail view
    const { unlinkSupplier: odooUnlink } = useOdooApi();
    const unlinkingFromDetail = ref(false);
    const handleUnlinkFromDetail = async () => {
      if (!document.value?.odooId) return;
      unlinkingFromDetail.value = true;
      try {
        await odooUnlink(document.value.odooId, props.id);
        showUnlinkConfirm.value = false;
        showToast("success", "Supplier unlinked from Odoo");
      } catch (e) {
        showToast("error", "Failed to unlink supplier from Odoo. Please try again.");
      } finally {
        unlinkingFromDetail.value = false;
      }
    };

    return {
      document,
      fetchError,
      notFound,
      subscribe,
      center,
      locationColumns,
      containerColumns,
      pastOrderColumns,
      pastOrders,
      loadingPastOrders,
      pastOrdersError,
      showForm,
      containerToDelete,
      deletingContainer,
      confirmDeleteContainer,
      deleteContainer,
      hasRecurring,
      fixedMode,
      fixedWeekdays,
      weekdayChips,
      toggleFixedDay,
      recurringFrequency,
      updateReccuring,
      recurringStartDate,
      dateList,
      recurringButtonLoading,
      recurringDuration,
      timeWindows,
      serviceStartTime,
      serviceEndTime,
      serviceTimeButtonLoading,
      serviceWindowError,
      updateServiceWindows,
      unlinkingFromDetail,
      handleUnlinkFromDetail,
      showUnlinkConfirm,
      showEditSupplier,
      cancellingRecurring,
      cancelRecurring,
      showCancelRecurringConfirm,
      isDark,
      collectionStatus,
      collectionStatusBusy,
      toggleCollectionStatus,
    };
  },
};
</script>

<style scoped>
.supplier-view > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.supplier-view > *:nth-child(1) { animation-delay: 0s; }
.supplier-view > *:nth-child(2) { animation-delay: 0.06s; }

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .supplier-view > * { animation: none; }
}
</style>
